import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Section, Card, Badge, Button, Alert } from "../design-system";
import { PfaTiersCompact } from "../components/PfaTiers";
import { PfaScopeSummary } from "../components/PfaScopeSummary";
import { useAuth } from "../auth/AuthProvider";
import { useCart } from "../data/cartStore";
import { Phone, MessageCircle, Mail, ImageOff, Truck, AlertTriangle, Bell, Info, Paperclip, ExternalLink, BookOpen } from "lucide-react";

const STATUS_LABEL = {
  RECEIVED: "Ricevuto", WAITING_PAYMENT: "Attesa pagamento",
  PROCESSING: "In lavorazione", SHIPPING: "In spedizione",
  IN_DELIVERY: "In consegna",
  DELIVERED: "Consegnato", READY_FOR_PICKUP: "Pronto ritiro",
};

const STATUS_TONE = {
  RECEIVED: "info", WAITING_PAYMENT: "warning", PROCESSING: "info",
  SHIPPING: "info", IN_DELIVERY: "info", DELIVERED: "success",
};

const SHIPMENT_STATUS_LABEL = {
  DRAFT: "In preparazione", IN_TRANSIT: "In consegna",
  DELIVERED: "Consegnata", CLOSED: "Chiusa",
};

const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu",
                   "lug", "ago", "set", "ott", "nov", "dic"];

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : ""; }

// --- Reorder suggestions from customer_purchase_history --------------
function ReorderSuggestionsSection() {
  const { apiGet } = useAuth();
  const nav = useNavigate();
  const addToCart = useCart((s) => s.add);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [addedSku, setAddedSku] = useState(null);

  useEffect(() => {
    apiGet("/api/customer/reorder-suggestions?limit=6")
      .then((r) => setData(r))
      .catch((e) => setErr(e?.data?.detail || "Errore suggerimenti"));
  }, [apiGet]);

  if (err) return null;                          // fallback silenzioso (feature secondaria)
  if (!data || !data.suggestions?.length) return null;

  const onAdd = (s) => {
    addToCart({
      id: s.product_id, sku: s.sku, name: s.description,
      kg_per_carton: s.kg_per_carton,
    }, 1);
    setAddedSku(s.sku);
    setTimeout(() => setAddedSku(null), 1500);
  };

  return (
    <div data-testid="reorder-suggestions" style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <strong style={{ fontSize: ".92rem" }}>Suggeriti per te</strong>
        <span className="muted" style={{ fontSize: ".72rem" }}>dallo storico acquisti</span>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {data.suggestions.map((s) => (
          <li key={s.product_id} data-testid={`sugg-${s.sku}`}
              style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9",
                       display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <strong style={{ fontSize: ".85rem" }}>{s.description}</strong>
                {s.frequently_bought && (
                  <Badge tone="info" data-testid={`sugg-badge-${s.sku}`}>Acquistato spesso</Badge>
                )}
              </div>
              <div className="muted" style={{ fontSize: ".72rem" }}>
                SKU {s.sku} · ultima acquisto {fmtDate(s.last_purchase_date)}
              </div>
            </div>
            <Button size="sm" variant={addedSku === s.sku ? "outline" : "primary"}
                    data-testid={`sugg-add-${s.sku}`}
                    onClick={() => onAdd(s)}>
              {addedSku === s.sku ? "✓ Aggiunto" : "Aggiungi"}
            </Button>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 8 }}>
        <Button size="sm" variant="outline" onClick={() => nav("/customer/catalog")}
                data-testid="sugg-goto-catalog">Vai al catalogo →</Button>
      </div>
    </div>
  );
}
function fmtEur(n) {
  const v = Number(n || 0);
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR",
                                     minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtDiscount(cp) {
  const sign = cp.discount_type === "PERCENT" ? "%" : "€";
  return `-${cp.value}${sign}`;
}
function describeTarget(cp) {
  // Feb 2026 — Promo con trigger "primo acquisto nuovo SKU" NON è "Tutti i prodotti"
  // né un prodotto/categoria specifico: è una promo condizionata all'evento
  // di nuovo inserimento. La UI deve distinguere trigger commerciale da ambito.
  if (cp.trigger_type === "FIRST_PURCHASE_NEW_SKU") {
    let sub;
    if (cp.new_product_promo_scope === "TRIGGER_SKU_ONLY") {
      sub = "Sconto sul prodotto di nuovo inserimento";
    } else if (cp.new_product_promo_scope === "ALL_CUSTOMER_PURCHASES") {
      const window = (cp.duration_value && cp.duration_unit)
        ? ` per ${cp.duration_value} ${
            cp.duration_unit === "DAYS" ? "giorni"
            : cp.duration_unit === "WEEKS" ? "settimane"
            : cp.duration_unit === "MONTHS" ? "mesi"
            : cp.duration_unit}`
        : "";
      sub = `Dal primo acquisto di un nuovo SKU${window}, sconto sugli acquisti eleggibili`;
    } else {
      sub = "Attivo al primo acquisto di un nuovo prodotto";
    }
    return { label: "Nuovo inserimento", kind: "new_sku", sub };
  }
  if (cp.product_name) return { label: cp.product_name, kind: "prodotto" };
  if (cp.category_name) return { label: cp.category_name, kind: "categoria" };
  return { label: "Tutti i prodotti", kind: "tutti" };
}

// --- ProductThumb with placeholder --------------------------------------
function ProductThumb({ src, alt = "", size = 48 }) {
  const [ok, setOk] = useState(!!src);
  const box = { width: size, height: size, borderRadius: 6, flexShrink: 0 };
  if (!src || !ok) {
    return (
      <div style={{ ...box, background: "#f1f5f9", display: "flex",
                    alignItems: "center", justifyContent: "center", color: "#94a3b8" }}
           aria-hidden="true">
        <ImageOff size={Math.round(size / 2.4)} />
      </div>
    );
  }
  return (
    <img src={src} alt={alt} onError={() => setOk(false)}
         style={{ ...box, objectFit: "cover" }} />
  );
}

// --- Shipping Tiers card ------------------------------------------------
function ShippingTiersCard({ tiersByRegion, deliveryPoints, defaultDpId }) {
  const regions = Object.keys(tiersByRegion);
  const initialDp = deliveryPoints.find((d) => d.id === defaultDpId) || deliveryPoints[0];
  const [dpId, setDpId] = useState(initialDp?.id || null);
  const currentDp = deliveryPoints.find((d) => d.id === dpId) || initialDp;
  const region = currentDp?.region;
  const tiers = (region && tiersByRegion[region]) || [];

  if (regions.length === 0) {
    return (
      <Card data-testid="tile-shipping-tiers">
        <Card.Header>
          <strong>Spese di trasporto — la tua regione</strong>
        </Card.Header>
        <Card.Body>
          <p className="muted">Nessuna regola di trasporto configurata per le tue sedi.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card data-testid="tile-shipping-tiers">
      <Card.Header>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Truck size={18} aria-hidden="true" />
          <strong>Spese di trasporto — {region || "—"}</strong>
        </div>
      </Card.Header>
      <Card.Body>
        {deliveryPoints.length > 1 && (
          <div style={{ marginBottom: 10 }}>
            <label htmlFor="dp-select-tiers" className="muted"
                   style={{ fontSize: ".8rem", display: "block", marginBottom: 4 }}>
              Sede di consegna
            </label>
            <select id="dp-select-tiers"
                    data-testid="shipping-tiers-dp-select"
                    value={dpId || ""}
                    onChange={(e) => setDpId(e.target.value)}
                    style={{ width: "100%", padding: "6px 8px", borderRadius: 6,
                             border: "1px solid #cbd5e1" }}>
              {deliveryPoints.map((dp) => (
                <option key={dp.id} value={dp.id}>
                  {dp.name} — {dp.city} ({dp.province}) · {dp.region}
                </option>
              ))}
            </select>
          </div>
        )}
        {tiers.length === 0 ? (
          <p className="muted">Nessuno scaglione configurato per {region}.</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {tiers.map((t) => {
              const isFree = t.free_shipping_threshold !== null && t.free_shipping_threshold !== undefined;
              const min = t.min_order_amount || 0;
              const max = t.max_order_amount;
              const range = max !== null && max !== undefined
                ? `${fmtEur(min)} – ${fmtEur(max)}`
                : `oltre ${fmtEur(min)}`;
              return (
                <li key={t.id}
                    data-testid={`shipping-tier-${t.id}`}
                    style={{ padding: "8px 0", borderBottom: "1px solid #eee",
                             display: "flex", justifyContent: "space-between",
                             alignItems: "center", gap: 8 }}>
                  <div>
                    <div><strong>{range}</strong></div>
                    {isFree && (
                      <div className="muted" style={{ fontSize: ".78rem" }}>
                        franco a partire da {fmtEur(t.free_shipping_threshold)}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div><strong>{t.transport_cost > 0 ? fmtEur(t.transport_cost) : "Franco"}</strong></div>
                    <div className="muted" style={{ fontSize: ".72rem" }}>trasporto</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <p className="muted" style={{ fontSize: ".78rem", marginTop: 8, marginBottom: 0 }}>
          Le condizioni finali (sconti, franco, promozioni) sono calcolate al carrello.
        </p>
      </Card.Body>
    </Card>
  );
}

// --- Announcements card ------------------------------------------------
const PRIORITY_STYLE = {
  URGENT: { border: "2px solid #dc2626", background: "#fef2f2", tone: "danger", label: "Urgente" },
  IMPORTANT: { border: "1px solid #f59e0b", background: "#fffbeb", tone: "warning", label: "Importante" },
  NORMAL: { border: "1px solid #e5e7eb", background: "#fff", tone: "info", label: "Normale" },
};

function PriorityIconLocal({ priority, size = 16 }) {
  if (priority === "URGENT") return <AlertTriangle size={size} aria-hidden="true" />;
  if (priority === "IMPORTANT") return <Bell size={size} aria-hidden="true" />;
  return <Info size={size} aria-hidden="true" />;
}

function AnnouncementsCard({ data }) {
  const items = data?.items || [];
  const total = data?.total || 0;
  const hasUrgent = items.some((a) => a.priority === "URGENT");
  return (
    <Card data-testid="tile-announcements"
          style={hasUrgent ? { borderLeft: "5px solid #dc2626" } : undefined}>
      <Card.Header>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Bell size={18} aria-hidden="true" />
            <strong>Comunicazioni importanti</strong>
          </div>
          {total > 0 && <Badge tone="neutral">{total}</Badge>}
        </div>
      </Card.Header>
      <Card.Body>
        {items.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nessuna comunicazione attiva.</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {items.map((a) => {
              const st = PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.NORMAL;
              return (
                <li key={a.id}
                    data-testid={`announcement-${a.id}`}
                    style={{ padding: 10, marginBottom: 8, borderRadius: 6,
                             border: st.border, background: st.background }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                                alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6,
                                  minWidth: 0 }}>
                      <PriorityIconLocal priority={a.priority} size={16} />
                      <strong style={{ overflow: "hidden", textOverflow: "ellipsis",
                                       whiteSpace: "nowrap" }}>{a.title}</strong>
                    </div>
                    <Badge tone={st.tone}>{st.label}</Badge>
                  </div>
                  <div style={{ fontSize: ".85rem", color: "#374151",
                                display: "-webkit-box", WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {a.summary || ""}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                    <Link to={`/customer/announcements/${a.id}`}
                          data-testid={`announcement-open-${a.id}`}>
                      <Button size="sm">Apri</Button>
                    </Link>
                    {a.has_attachment && (
                      <Badge tone="neutral"><Paperclip size={12} /> allegato</Badge>
                    )}
                    {a.link_url && (
                      <Badge tone="neutral"><ExternalLink size={12} /> link</Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {total > items.length && (
          <div style={{ marginTop: 8 }}>
            <Link to="/customer/announcements">
              <Button size="sm" data-testid="announcements-view-all">
                Vedi tutte ({total}) →
              </Button>
            </Link>
          </div>
        )}
        {items.length > 0 && total <= items.length && (
          <div style={{ marginTop: 8 }}>
            <Link to="/customer/announcements">
              <Button size="sm" data-testid="announcements-view-all">Vedi tutte →</Button>
            </Link>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

// --- Compact agent card (Feb 2026) — top-right nell'header Home.
// Fix "None None": costruisce il display name SOLO dai valori realmente
// presenti (o usa il full_name se pulito, altrimenti fallback neutro).
function displayAgentName(agent) {
  if (!agent) return "";
  // Feb 2026 v34 — preferisci display_name (nome commerciale agenzia).
  // Fallback: first_name+last_name (persone fisiche), poi full_name, poi "Agente".
  const display = (agent.display_name || "").trim();
  if (display) return display;
  const first = (agent.first_name || "").trim();
  const last = (agent.last_name || "").trim();
  const composed = [first, last].filter(Boolean).join(" ").trim();
  if (composed) return composed;
  const raw = (agent.full_name || "").trim();
  const isPlaceholder = /^(?:none|null|undefined)(?:\s+(?:none|null|undefined))*$/i.test(raw) || raw === "";
  return isPlaceholder ? "Agente" : raw;
}

function normalizeWhatsapp(num) {
  if (!num) return null;
  const cleaned = String(num).replace(/[\s().\-+]/g, "");
  return /^\d{6,15}$/.test(cleaned) ? cleaned : null;
}

function ContactActions({ email, phone, whatsapp, testidPrefix }) {
  const wa = normalizeWhatsapp(whatsapp);
  const btnStyle = {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 8px", borderRadius: 6, fontSize: ".72rem",
    textDecoration: "none", border: "1px solid #E2E8F0",
    background: "#fff", color: "#0F4C81",
  };
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
      {email && (
        <a href={`mailto:${email}`}
           data-testid={`${testidPrefix}-email`}
           style={btnStyle} title={email}>
          <Mail size={12} aria-hidden="true" /> Email
        </a>
      )}
      {phone && (
        <a href={`tel:${String(phone).replace(/\s/g, "")}`}
           data-testid={`${testidPrefix}-phone`}
           style={btnStyle} title={phone}>
          <Phone size={12} aria-hidden="true" /> Chiama
        </a>
      )}
      {wa && (
        <a href={`https://wa.me/${wa}`}
           target="_blank" rel="noopener noreferrer"
           data-testid={`${testidPrefix}-whatsapp`}
           style={{ ...btnStyle, borderColor: "#22C55E", color: "#166534" }}
           title={`WhatsApp ${whatsapp}`}>
          <MessageCircle size={12} aria-hidden="true" /> WhatsApp
        </a>
      )}
    </div>
  );
}

function CompactAgentCard({ agent }) {
  if (!agent) return null;
  const name = displayAgentName(agent);
  const initials = name.split(/\s+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "AG";
  // Feb 2026 v33 — Contatti persona (fino a 2). Solo quelli valorizzati sono
  // renderizzati; email agenzia resta come recapito generale in cima.
  const contacts = Array.isArray(agent.contacts) ? agent.contacts.filter(
    (c) => c && (c.first_name || c.last_name || c.email || c.phone || c.whatsapp)
  ) : [];
  const contactName = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || "Referente";
  return (
    <div data-testid="agent-compact"
         style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px",
                  border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff",
                  maxWidth: 340 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {agent.photo_url ? (
          <img src={agent.photo_url} alt="" data-testid="agent-compact-avatar"
               style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
        ) : (
          <div data-testid="agent-compact-initials"
               style={{ width: 34, height: 34, borderRadius: "50%", background: "#e2e8f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#0F4C81", fontWeight: 700, fontSize: ".8rem", flexShrink: 0 }}>
            {initials}
          </div>
        )}
        <div style={{ minWidth: 0, lineHeight: 1.25, flex: 1 }}>
          <div className="muted" style={{ fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".05em" }}>
            Il tuo agente
          </div>
          <div data-testid="agent-compact-name"
               style={{ fontSize: ".9rem", fontWeight: 600,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </div>
          {agent.email && (
            <a href={`mailto:${agent.email}`} data-testid="agent-compact-email"
               style={{ display: "inline-flex", alignItems: "center", gap: 4,
                        color: "#0F4C81", textDecoration: "none", fontSize: ".75rem",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: 260 }}
               title="Email agenzia">
              <Mail size={12} aria-hidden="true" /> {agent.email}
            </a>
          )}
        </div>
      </div>
      {contacts.map((c, idx) => (
        <div key={idx} data-testid={`agent-contact-${idx + 1}-card`}
             style={{ paddingTop: 6, borderTop: "1px dashed #e5e7eb" }}>
          <div style={{ fontSize: ".82rem", fontWeight: 600 }}
               data-testid={`agent-contact-${idx + 1}-name`}>
            {contactName(c)}
          </div>
          <ContactActions email={c.email} phone={c.phone} whatsapp={c.whatsapp}
                          testidPrefix={`agent-contact-${idx + 1}`} />
        </div>
      ))}
    </div>
  );
}

// --- Main ---------------------------------------------------------------
export default function CustomerHome() {
  const { apiGet, user } = useAuth();
  const cart = useCart();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  // v41 Step 3 — Multi-PFA summary. `null` = loading, [] = none, arrays otherwise.
  const [pfas, setPfas] = useState(null);
  const [dpChoice, setDpChoice] = useState(false);
  const [addedFlash, setAddedFlash] = useState(null);
  // v34.8 — Anticipated CTA verifica profilo (server-side gate rimane
  // CUSTOMER_PROFILE_VERIFICATION_REQUIRED). Nessun nuovo flag, riuso endpoint.
  const [profileVerificationRequired, setProfileVerificationRequired] = useState(false);

  useEffect(() => {
    apiGet("/api/customer/home").then(setD).catch((e) => setErr(e?.message || String(e)));
    // v41 Step 3 — Multi-PFA: fetch full PFA summary list for N>1 rendering.
    apiGet("/api/customer/pfa/summary").then((r) => setPfas(Array.isArray(r) ? r : []))
      .catch(() => setPfas([]));
    // Fetch parallelo: se l'endpoint fallisce (es. utente non-CUSTOMER), silenzia.
    apiGet("/api/customer/account/profile")
      .then((p) => setProfileVerificationRequired(!!p?.profile_verification_required))
      .catch(() => {});
  }, []);

  const goNewOrder = () => {
    if (!d) return;
    if (d.delivery_points.length <= 1 || d.default_delivery_point_id) {
      nav("/customer/catalog");
    } else {
      setDpChoice(true);
    }
  };

  const addToCart = (p, qty = 1) => {
    cart.add({ id: p.product_id, sku: p.sku, name: p.name,
               kg_per_carton: p.kg_per_carton ?? 0 }, qty);
    setAddedFlash(p.product_id);
    setTimeout(() => setAddedFlash(null), 1500);
  };

  if (err) return <Section title="Home"><Alert tone="danger">Errore: {err}</Alert></Section>;
  if (!d) return <Section title="Home"><Card><Card.Body className="muted">Caricamento…</Card.Body></Card></Section>;

  return (
    <>
      {/* HERO with primary CTA + agent compatto (desktop: top-right, mobile: sotto) */}
      <Section title={`Ciao ${user?.full_name?.split(" ")[0] || d.customer.business_name}`}
               subtitle={d.customer.business_name}
               actions={<CompactAgentCard agent={d.agent} />}
               data-testid="customer-home-hero">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <Button size="lg" variant="primary" onClick={goNewOrder}
                  data-testid="cta-new-order"
                  style={{ fontSize: "1.05rem", padding: "12px 28px" }}>
            + Nuovo ordine
          </Button>
          <Link to="/customer/catalog"><Button data-testid="cta-catalog">Catalogo</Button></Link>
          <Link to="/customer/cart"><Button data-testid="cta-cart">Carrello ({cart.lines.length})</Button></Link>
          <Link to="/customer/orders"><Button data-testid="cta-orders">I miei ordini</Button></Link>
          <Link to="/customer/account"><Button data-testid="cta-account" variant="outline">Il mio account</Button></Link>
          <Link to="/customer/import-order"><Button data-testid="cta-import-order" variant="outline">Importa ordine</Button></Link>
        </div>
        <div data-testid="cta-import-order-card"
             style={{ marginTop: 14, padding: "12px 16px",
                      background: "#F0F9FF", border: "1px solid #BAE6FD",
                      borderRadius: 10, display: "flex", flexWrap: "wrap",
                      alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 700, color: "#075985" }}>
              Hai già un ordine dal tuo gestionale?
            </div>
            <div style={{ fontSize: ".9rem", color: "#0C4A6E", marginTop: 2 }}>
              Carica PDF, Excel o CSV e prepariamo automaticamente l'ordine.
            </div>
          </div>
          <Link to="/customer/import-order">
            <Button size="md" variant="primary" data-testid="cta-import-order-btn">
              IMPORTA ORDINE →
            </Button>
          </Link>
        </div>
        {/* v34.8 — Banner verifica profilo obbligatoria (Customer non verificato).
            Il gate reale rimane server-side (CUSTOMER_PROFILE_VERIFICATION_REQUIRED);
            questo banner anticipa e spiega il requisito. */}
        {profileVerificationRequired && (
          <div data-testid="profile-verification-hero"
               style={{ marginTop: 14, padding: "18px 20px",
                        background: "#FFFBEB", border: "2px solid #B45309",
                        borderRadius: 12, display: "flex", flexWrap: "wrap",
                        alignItems: "center", gap: 14 }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#7C2D12" }}>
                Prima di iniziare, verifica i dati della tua azienda
              </div>
              <div style={{ fontSize: ".95rem", color: "#78350F", marginTop: 4 }}>
                Controlla ragione sociale, indirizzo e dati fiscali. Se sono corretti, confermali.
                Se trovi qualcosa da modificare, puoi inviare una richiesta di aggiornamento.
              </div>
            </div>
            <Link to="/customer/account">
              <Button variant="primary" size="lg" data-testid="profile-verification-cta"
                      style={{ fontWeight: 800 }}>
                VERIFICA ORA
              </Button>
            </Link>
          </div>
        )}
        {/* Feb 2026 v33 — Banner orizzontale distinto dal pulsante "Catalogo"
            portale. Nasconde se REACT_APP_FLIPBOOK_CATALOG_URL non è configurata. */}
        {process.env.REACT_APP_FLIPBOOK_CATALOG_URL && (
          <a href={process.env.REACT_APP_FLIPBOOK_CATALOG_URL}
             target="_blank" rel="noopener noreferrer"
             data-testid="hero-flipbook-banner"
             style={{ display: "flex", alignItems: "center", gap: 14,
                      marginTop: 14, padding: "12px 16px",
                      background: "linear-gradient(90deg, #FFF7ED 0%, #FFEDD5 100%)",
                      border: "1px solid #FDBA74", borderRadius: 10,
                      textDecoration: "none", color: "#7C2D12",
                      boxShadow: "0 1px 2px rgba(0,0,0,.04)" }}>
            <BookOpen size={28} aria-hidden="true" style={{ flexShrink: 0, color: "#C2410C" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "1rem" }}>Catalogo sfogliabile GE SURGELATI</div>
              <div style={{ fontSize: ".85rem", opacity: .85 }}>
                Sfoglia il catalogo completo in nuova scheda (Calaméo)
              </div>
            </div>
            <span data-testid="flipbook-catalog-cta"
                  style={{ background: "#C2410C", color: "white",
                           padding: "8px 16px", borderRadius: 6, fontWeight: 600,
                           fontSize: ".9rem", whiteSpace: "nowrap", flexShrink: 0 }}>
              Apri catalogo ↗
            </span>
          </a>
        )}
      </Section>

      {/* GRID */}
      <div className="home-grid" style={{ display: "grid", gap: 16,
                                          gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))" }}
           data-testid="customer-home-grid">
        {/* Announcements */}
        <AnnouncementsCard data={d.announcements || { items: [], total: 0 }} />

        {/* Open Orders */}
        <Card data-testid="tile-open-orders">
          <Card.Header><strong>Ordini aperti</strong></Card.Header>
          <Card.Body>
            {d.open_orders.count === 0 ? (
              <p className="muted">Non hai ordini in lavorazione.</p>
            ) : (
              <>
                <div style={{ fontSize: "2rem", fontWeight: 700 }}>{d.open_orders.count}</div>
                <div className="muted" style={{ marginBottom: 8 }}>ordini attivi</div>
                {d.open_orders.waiting_payment && (
                  <Alert tone="warning" data-testid="alert-waiting-payment">
                    <div style={{ fontSize: ".75rem", textTransform: "uppercase",
                                  letterSpacing: ".05em", opacity: .8, marginBottom: 2 }}>
                      Ordine in evidenza
                    </div>
                    Ordine <strong>{d.open_orders.waiting_payment.number}</strong> in attesa di pagamento
                  </Alert>
                )}
                {d.open_orders.in_transit && (
                  <Alert tone="info" data-testid="alert-in-transit">
                    <div style={{ fontSize: ".75rem", textTransform: "uppercase",
                                  letterSpacing: ".05em", opacity: .8, marginBottom: 2 }}>
                      Ordine più recente
                    </div>
                    Ordine <strong>{d.open_orders.in_transit.number}</strong> in consegna
                  </Alert>
                )}
                {(() => {
                  const highlighted = new Set([
                    d.open_orders.waiting_payment?.id,
                    d.open_orders.in_transit?.id,
                  ]);
                  const shown = d.open_orders.items.filter((o) => !highlighted.has(o.id)).slice(0, 3);
                  return (
                    <>
                      <div style={{ marginTop: 8 }}>
                        {shown.map((o) => (
                          <Link key={o.id} to={`/customer/orders/${o.id}`}
                                data-testid={`open-order-${o.number}`}
                                style={{ display: "block", padding: "6px 0",
                                         textDecoration: "none", color: "inherit" }}>
                            <strong>{o.number}</strong>{" "}
                            <Badge tone={STATUS_TONE[o.status] || "info"}>{STATUS_LABEL[o.status]}</Badge>
                          </Link>
                        ))}
                      </div>
                      {d.open_orders.count > shown.length && (
                        <div className="muted" style={{ fontSize: ".85rem" }}>
                          Ultimi {shown.length} di {d.open_orders.count} ordini attivi
                        </div>
                      )}
                    </>
                  );
                })()}
                <Link to="/customer/orders">
                  <Button size="sm" style={{ marginTop: 8 }}>Vedi ordini →</Button>
                </Link>
              </>
            )}
          </Card.Body>
        </Card>

        {/* v41 Step 3 — Multi-PFA "Premi Fine Anno" mini-cards when N > 1. */}
        {pfas && pfas.length > 1 && (
          <Card data-testid="tile-pfa-multi">
            <Card.Header>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong>Premi Fine Anno</strong>
                <Link to="/customer/pfa" data-testid="pfa-multi-view-all">
                  <Button size="sm" variant="ghost">Vedi tutti →</Button>
                </Link>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                            gap: 10 }}>
                {pfas.map((p) => {
                  const matured = Number(p.matured_kg) || 0;
                  const target = Number(p.target_kg) || 0;
                  const nextMissing = p.next_tier_missing_kg
                                       != null ? Number(p.next_tier_missing_kg) : null;
                  // Mar 2026 UX — quando il PFA ha scaglioni, mostra come
                  // obiettivo principale il prossimo scaglione (matured + missing),
                  // non il target finale. Se non ci sono scaglioni o sono tutti
                  // raggiunti, fallback al target commerciale.
                  const hasNextTier = nextMissing != null && nextMissing > 0;
                  const displayGoal = hasNextTier ? matured + nextMissing : target;
                  const pct = displayGoal > 0
                    ? Math.min(100, Math.round(matured / displayGoal * 100))
                    : 0;
                  return (
                    <div key={p.pfa_id}
                         data-testid={`pfa-multi-card-${p.pfa_id}`}
                         style={{ padding: 10, border: "1px solid #E2E8F0",
                                  borderRadius: 8, background: "#FFFFFF",
                                  minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, wordBreak: "break-word" }}
                           data-testid={`pfa-multi-name-${p.pfa_id}`}>
                        {p.display_name || p.name || "PFA"}
                      </div>
                      <PfaScopeSummary pfa={p} maxChips={2}
                                       testidPrefix={`pfa-multi-scope-${p.pfa_id}`} />
                      <div style={{ marginTop: 6, fontSize: 12, color: "#334155" }}>
                        <strong data-testid={`pfa-multi-matured-${p.pfa_id}`}>
                          {matured.toLocaleString("it-IT")} kg
                        </strong>
                        {" / "}
                        <span className="muted"
                              data-testid={`pfa-multi-goal-${p.pfa_id}`}>
                          {displayGoal.toLocaleString("it-IT")} kg
                        </span>
                        {" · "}
                        <span data-testid={`pfa-multi-pct-${p.pfa_id}`}>{pct}%</span>
                      </div>
                      <div style={{ background: "#e5e7eb", borderRadius: 4,
                                    marginTop: 6, height: 5, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%",
                                      background: pct >= 100 ? "#10b981"
                                                : pct >= 75  ? "#0ea5e9"
                                                : "#0F4C81" }} />
                      </div>
                      <div style={{ marginTop: 6, fontSize: 11, color: "#64748B" }}>
                        {(p.tiers_total || 0) === 0
                          ? "Nessuno scaglione configurato"
                          : (
                            <>
                              {(p.tiers_reached || 0)}/{p.tiers_total || 0} scaglioni
                              {hasNextTier && (
                                <span>
                                  {" · "}
                                  {nextMissing.toLocaleString("it-IT")} kg al prossimo scaglione
                                </span>
                              )}
                            </>
                          )}
                      </div>
                      {hasNextTier && target !== displayGoal && (
                        <div style={{ marginTop: 4, fontSize: 10, color: "#94A3B8" }}
                             data-testid={`pfa-multi-final-target-${p.pfa_id}`}>
                          Obiettivo finale: {target.toLocaleString("it-IT")} kg
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* PFA — compatto: singolo PFA (N=0 legacy, N=1 UX compatta). */}
        {(!pfas || pfas.length <= 1) && (
        <Card data-testid="tile-pfa">
          <Card.Header><strong>PFA maturato</strong></Card.Header>
          <Card.Body>
            {!d.pfa ? (
              <p className="muted" style={{ margin: 0 }}>Nessun contratto PFA attivo.</p>
            ) : d.pfa.has_tiers ? (
              // Feb 2026 — Rappresentazione a scaglioni (business friendly)
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8,
                              flexWrap: "wrap" }}>
                  <div style={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1 }}
                       data-testid="pfa-matured-kg">
                    {d.pfa.matured_kg.toLocaleString("it-IT")} kg
                  </div>
                  <div className="muted" style={{ fontSize: ".85rem" }}>maturati</div>
                </div>
                <div style={{ marginTop: 6, fontSize: ".92rem" }}
                     data-testid="pfa-tiers-line">
                  <Badge tone={d.pfa.tiers_reached >= d.pfa.tiers_total
                               ? "success" : "info"}>
                    {d.pfa.tiers_reached}/{d.pfa.tiers_total} scaglioni raggiunti
                  </Badge>{" "}
                  {d.pfa.cumulative_reward_percent > 0 && (
                    <span style={{ marginLeft: 4 }}>
                      Premio maturato:{" "}
                      <strong data-testid="pfa-reward-pct">
                        {Number(d.pfa.cumulative_reward_percent).toLocaleString("it-IT", {
                          minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </strong>
                    </span>
                  )}
                </div>
                <div style={{ background: "#e5e7eb", borderRadius: 4, marginTop: 8,
                              height: 6, overflow: "hidden" }}>
                  <div style={{
                    width: `${Math.min(100,
                      d.pfa.tiers_total > 0
                        ? Math.round((d.pfa.tiers_reached / d.pfa.tiers_total) * 100)
                        : 0)}%`,
                    background: d.pfa.tiers_reached >= d.pfa.tiers_total
                                ? "#10b981" : "#0F4C81",
                    height: "100%" }} />
                </div>
                <div style={{ marginTop: 8, display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span className="muted" style={{ fontSize: ".82rem" }}
                        data-testid="pfa-next-tier">
                    {d.pfa.tiers_reached >= d.pfa.tiers_total
                      ? "Tutti gli scaglioni raggiunti"
                      : d.pfa.next_tier_missing_kg
                        ? `${Number(d.pfa.next_tier_missing_kg).toLocaleString("it-IT")} kg al prossimo scaglione`
                        : "In corso"}
                  </span>
                  <Link to="/customer/orders" data-testid="pfa-view-detail">
                    <Button size="sm" variant="ghost">Vedi dettaglio →</Button>
                  </Link>
                  <Link to="/customer/contracts" data-testid="pfa-view-contracts">
                    <Button size="sm" variant="ghost">Vedi tutti i contratti →</Button>
                  </Link>
                </div>
                {d.pfa.tiers && d.pfa.tiers.length > 0 && (
                  <PfaTiersCompact
                    tiers={d.pfa.tiers}
                    maturedKg={Number(d.pfa.matured_kg) || 0}
                    testidPrefix="pfa-tier"
                  />
                )}
                {d.pfa.requires_new_product_for_payout && (
                  <div data-testid="pfa-new-product-card"
                       style={{ marginTop: 10, padding: "10px 12px",
                                background: d.pfa.new_product_requirement_met
                                  ? "#ECFDF5" : "#FFFBEB",
                                border: d.pfa.new_product_requirement_met
                                  ? "1px solid #A7F3D0" : "1px solid #FDE68A",
                                borderRadius: 6 }}>
                    <div style={{ display: "flex", alignItems: "center",
                                  justifyContent: "space-between", gap: 8,
                                  flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 600, fontSize: ".92rem",
                                    color: d.pfa.new_product_requirement_met
                                      ? "#065F46" : "#92400E" }}>
                        {d.pfa.new_product_requirement_met
                          ? "Nuovo inserimento completato"
                          : "Nuovo inserimento richiesto"}
                      </div>
                      <Badge tone={d.pfa.new_product_requirement_met
                                   ? "success" : "warning"}>
                        {d.pfa.new_product_requirement_met
                          ? "Completato ✓" : "Da completare"}
                      </Badge>
                    </div>
                    <div style={{ marginTop: 6, fontSize: ".82rem",
                                  color: d.pfa.new_product_requirement_met
                                    ? "#047857" : "#78350F", lineHeight: 1.4 }}>
                      {d.pfa.new_product_requirement_met
                        ? "Requisito per l'erogazione del premio soddisfatto."
                        : "Il tuo PFA sta maturando regolarmente, ma il premio potrà essere erogato solo dopo l'inserimento di almeno un nuovo prodotto (mai acquistato prima)."}
                    </div>
                    {d.pfa.new_product_requirement_met && d.pfa.new_product_name && (
                      <div data-testid="pfa-new-product-detail"
                           style={{ marginTop: 8, paddingTop: 6,
                                    borderTop: "1px dashed #A7F3D0",
                                    fontSize: ".82rem", color: "#065F46",
                                    lineHeight: 1.4 }}>
                        <div>
                          Prodotto:{" "}
                          <strong>{d.pfa.new_product_name}</strong>
                          {d.pfa.new_product_sku && (
                            <span style={{ color: "#047857" }}>
                              {" "}· SKU {d.pfa.new_product_sku}
                            </span>
                          )}
                        </div>
                        {d.pfa.new_product_first_purchase_date && (
                          <div style={{ marginTop: 2 }}>
                            Primo acquisto:{" "}
                            <strong>{fmtDate(d.pfa.new_product_first_purchase_date)}</strong>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              // PFA target singolo senza scaglioni: comportamento precedente invariato
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1 }}
                       data-testid="pfa-matured-kg">
                    {d.pfa.matured_kg.toLocaleString("it-IT")} kg
                  </div>
                  <div className="muted" style={{ fontSize: ".85rem" }}>
                    di {d.pfa.target_kg.toLocaleString("it-IT")} kg · {d.pfa.percentage}%
                  </div>
                </div>
                <div style={{ background: "#e5e7eb", borderRadius: 4, marginTop: 8,
                              height: 6, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(d.pfa.percentage, 100)}%`,
                                background: d.pfa.percentage >= 100 ? "#10b981" : "#0F4C81",
                                height: "100%" }} />
                </div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between",
                              alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <span className="muted" style={{ fontSize: ".78rem" }}>
                    {d.pfa.percentage >= 100
                      ? `+${(d.pfa.matured_kg - d.pfa.target_kg).toLocaleString("it-IT")} kg oltre target`
                      : `${d.pfa.residual_kg.toLocaleString("it-IT")} kg rimanenti`}
                  </span>
                  <Link to="/customer/orders" data-testid="pfa-view-detail">
                    <Button size="sm" variant="ghost">Vedi dettaglio →</Button>
                  </Link>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
        )}

        {/* Reorder + Suggerimenti — merged (Feb 2026): niente card Riordino
            separata. Il CTA riordino ultimo ordine sta nell'header di
            "Ultimi acquisti" (sotto), i suggerimenti storico restano qui. */}
        {d.last_order && (
          <ReorderSuggestionsSection />
        )}

        {/* Contract Promotions */}
        <Card data-testid="tile-contract-promos">
          <Card.Header><strong>Promo contrattuali</strong></Card.Header>
          <Card.Body>
            {d.contract_promotions.length === 0 ? (
              <p className="muted">Nessuna promozione contrattuale attiva.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {d.contract_promotions.slice(0, 6).map((cp) => {
                  const target = describeTarget(cp);
                  const validity = cp.contract_starts_at && cp.contract_ends_at
                    ? `${fmtDate(cp.contract_starts_at)} → ${fmtDate(cp.contract_ends_at)}`
                    : null;
                  return (
                    <li key={cp.id} data-testid={`cp-${cp.id}`}
                        style={{ padding: "8px 0", borderBottom: "1px solid #eee" }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                                    alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0 }}>
                          <div>
                            <Badge tone={
                              target.kind === "new_sku" ? "warning"
                              : target.kind === "tutti" ? "neutral" : "info"}>
                              {target.kind === "new_sku" ? "Nuovo inserimento"
                                : target.kind === "tutti" ? "Tutti i prodotti"
                                : target.kind === "categoria" ? "Categoria" : "Prodotto"}
                            </Badge>{" "}
                            <strong>{target.kind === "new_sku" ? null : target.label}</strong>
                          </div>
                          {target.sub && (
                            <div style={{ fontSize: ".82rem", color: "#334155",
                                          marginTop: 3 }}
                                 data-testid={`cp-${cp.id}-sub`}>
                              {target.sub}
                            </div>
                          )}
                          {cp.trigger_type === "FIRST_PURCHASE_NEW_SKU"
                           && cp.duration_value && cp.duration_unit && (
                            <div style={{ fontSize: ".78rem", color: "#0369A1",
                                          marginTop: 3, fontWeight: 500 }}
                                 data-testid={`cp-${cp.id}-duration`}>
                              Durata: {cp.duration_value}{" "}
                              {cp.duration_value === 1
                                ? (cp.duration_unit === "DAYS" ? "giorno"
                                   : cp.duration_unit === "WEEKS" ? "settimana"
                                   : cp.duration_unit === "MONTHS" ? "mese"
                                   : cp.duration_unit.toLowerCase())
                                : (cp.duration_unit === "DAYS" ? "giorni"
                                   : cp.duration_unit === "WEEKS" ? "settimane"
                                   : cp.duration_unit === "MONTHS" ? "mesi"
                                   : cp.duration_unit.toLowerCase())}
                              {" "}dal primo acquisto del nuovo SKU
                            </div>
                          )}
                          <div className="muted" style={{ fontSize: ".78rem", marginTop: 2 }}>
                            min. {cp.min_qty} cart.
                            {cp.month && ` · solo mese ${MONTHS_IT[cp.month - 1] || cp.month}`}
                            {validity && (cp.trigger_type === "FIRST_PURCHASE_NEW_SKU"
                              ? ` · attivabile entro ${fmtDate(cp.contract_ends_at)}`
                              : ` · valido ${validity}`)}
                          </div>
                        </div>
                        <Badge tone="success">
                          <span style={{ fontWeight: 700 }}>{fmtDiscount(cp)}</span>
                        </Badge>
                      </div>
                      {cp.contract_has_document && (
                        <div style={{ marginTop: 8 }}>
                          <button type="button"
                                  data-testid={`cp-${cp.id}-doc`}
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem("access_token");
                                      const r = await fetch(
                                        `${process.env.REACT_APP_BACKEND_URL}${cp.contract_document_url || `/api/customer/contracts/${cp.contract_id}/document`}`,
                                        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                                      if (!r.ok) { alert("Documento non disponibile"); return; }
                                      // Force application/pdf so il browser
                                      // apre il viewer inline (evita fallback a
                                      // download come "file generico").
                                      const raw = await r.blob();
                                      const pdfBlob = new Blob([raw], { type: "application/pdf" });
                                      const url = URL.createObjectURL(pdfBlob);
                                      window.open(url, "_blank", "noopener,noreferrer");
                                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                                    } catch (e) { alert("Errore apertura PDF"); }
                                  }}
                                  style={{ background: "transparent", border: "1px solid #94A3B8",
                                           color: "#334155", padding: "4px 10px",
                                           borderRadius: 6, fontSize: ".78rem", cursor: "pointer",
                                           display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <Paperclip size={14} aria-hidden="true" />
                            Visualizza contratto PDF
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card.Body>
        </Card>

        {/* General Promotions */}
        <Card data-testid="tile-general-promos">
          <Card.Header><strong>Promozioni attive</strong></Card.Header>
          <Card.Body>
            {d.general_promotions.length === 0 ? (
              <p className="muted">Nessuna promozione attiva applicabile.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {d.general_promotions.map((p) => {
                  // Feb 2026 v34 — Copy commerciale Customer:
                  // spiega meccanica, ambito prodotti, esclusioni, destinatari.
                  const fmt2 = (v) => Number(v).toLocaleString("it-IT",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  let mechanic = "Promozione";
                  if (p.discount_type === "CARTONI_OMAGGIO"
                        && p.buy_cartons && p.free_cartons) {
                    const b = p.buy_cartons, f = p.free_cartons;
                    const cart = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;
                    const buyTxt = cart(b, "cartone", "cartoni");
                    const freeTxt = cart(f, "cartone omaggio", "cartoni omaggio");
                    if (p.free_product_mode === "SAME_PRODUCT") {
                      mechanic = `Acquista ${buyTxt} e ricevi ${freeTxt} dello stesso articolo`;
                    } else if (p.free_product_mode === "SPECIFIC_PRODUCT" && p.free_product_name) {
                      mechanic = `Acquista ${buyTxt} e ricevi ${freeTxt} di ${p.free_product_name}`;
                    } else {
                      mechanic = `Acquista ${buyTxt} e ricevi ${freeTxt}`;
                    }
                  } else if (p.discount_type === "PERCENT") {
                    mechanic = `Sconto ${p.value}%`;
                  } else if (p.discount_type === "FIXED_PRICE") {
                    mechanic = `Prezzo promo ${fmt2(p.value)} €/kg`;
                  } else if (p.discount_type === "AMOUNT") {
                    mechanic = `Sconto ${fmt2(p.value)} €`;
                  }
                  // Ambito prodotti
                  let scopeLine = null;
                  let scopeList = null;
                  const scope = p.product_scope || "ALL_PRODUCTS";
                  if (scope === "ALL_PRODUCTS") {
                    scopeLine = "Valida su tutti i prodotti";
                  } else if (scope === "CATEGORY") {
                    scopeLine = `Valida sulla categoria: ${p.category_name || "—"}`;
                  } else if (scope === "SINGLE_PRODUCT") {
                    scopeLine = p.product_sku && p.product_name
                      ? `Valida su: ${p.product_sku} — ${p.product_name}`
                      : "Valida su un prodotto";
                  } else if (scope === "MULTI_PRODUCT") {
                    const items = p.products_named || [];
                    scopeLine = `Valida su ${items.length} prodotti`;
                    if (items.length > 0) {
                      const show = items.slice(0, 3);
                      const extra = items.length - show.length;
                      scopeList = (
                        <ul style={{ margin: "2px 0 0 16px", padding: 0, fontSize: ".78rem" }}>
                          {show.map((it, i) => (
                            <li key={i} className="muted">{it.sku} — {it.name}</li>
                          ))}
                          {extra > 0 && (
                            <li key="more" className="muted">+ altri {extra} prodott{extra === 1 ? "o" : "i"}</li>
                          )}
                        </ul>
                      );
                    }
                  }
                  // Destinatari (label compatta opzionale)
                  let audienceLabel = null;
                  if (p.target_type === "CATEGORY") audienceLabel = "Riservata alla tua categoria";
                  else if (p.target_type === "CUSTOMER") audienceLabel = "Promo dedicata";
                  return (
                    <li key={p.id} data-testid={`promo-${p.code}`}
                        style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                                    alignItems: "flex-start", gap: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          <div data-testid={`promo-mechanic-${p.code}`}
                               style={{ fontSize: ".85rem", marginTop: 2 }}>
                            {mechanic}
                          </div>
                          {scopeLine && (
                            <div data-testid={`promo-scope-${p.code}`}
                                 className="muted" style={{ fontSize: ".78rem", marginTop: 2 }}>
                              {scopeLine}
                            </div>
                          )}
                          {scopeList}
                          {p.excluded_count > 0 && (
                            <div className="muted"
                                 data-testid={`promo-excluded-${p.code}`}
                                 style={{ fontSize: ".78rem", marginTop: 2 }}>
                              Esclusi: {p.excluded_count} prodott{p.excluded_count === 1 ? "o" : "i"}
                            </div>
                          )}
                          <div className="muted" style={{ fontSize: ".78rem", marginTop: 2 }}>
                            fino al {fmtDate(p.ends_at)}
                          </div>
                        </div>
                        {audienceLabel && (
                          <Badge tone="neutral" data-testid={`promo-audience-${p.code}`}>
                            {audienceLabel}
                          </Badge>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card.Body>
        </Card>

        {/* v34 — Reorder nudge (era "Ultimi acquisti"). Mostra i prodotti che
            il cliente non acquista da almeno 60 giorni, oldest-first, max 5.
            Quick-add aggiunge 1 strato (cartons_per_layer). Se CPL manca →
            bottone disabilitato con link al catalogo. */}
        <Card data-testid="tile-recent-purchases">
          <Card.Header>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong data-testid="reorder-nudge-title">È da un po' che non li ordini</strong>
              {d.last_order && (
                <Link to={`/customer/orders/${d.last_order.id}`}>
                  <Button size="sm" variant="primary" data-testid="cta-reorder-last"
                          title={`Riordina ${d.last_order.number} — i prezzi verranno ricalcolati con le condizioni correnti.`}>
                    Riordina {d.last_order.number}
                  </Button>
                </Link>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {d.recent_purchases.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                Nessun prodotto da riordinare al momento.
              </p>
            ) : (
              <>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {d.recent_purchases.slice(0, 5).map((p) => {
                    const cpl = p.cartons_per_layer || 0;
                    const canQuickAdd = cpl > 0;
                    return (
                      <li key={p.product_id} data-testid={`recent-${p.sku}`}
                          style={{ padding: "6px 0", borderBottom: "1px solid #f1f5f9",
                                   display: "flex", justifyContent: "space-between",
                                   alignItems: "center", gap: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis",
                                        whiteSpace: "nowrap", fontSize: ".85rem", fontWeight: 500 }}>
                            {p.name}
                          </div>
                          <div className="muted" style={{ fontSize: ".72rem" }}>
                            {p.sku} · ultimo acquisto {fmtDate(p.last_purchase_date || p.last_purchased_at)}
                          </div>
                        </div>
                        {canQuickAdd ? (
                          <Button size="sm" variant="primary"
                                  onClick={() => addToCart(p, cpl)}
                                  data-testid={`recent-add-${p.sku}`}
                                  title={`Aggiunge 1 strato (${cpl} cartoni)`}
                                  style={{ flexShrink: 0 }}>
                            {addedFlash === p.product_id ? "✓" : `+${cpl}`}
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled
                                  data-testid={`recent-add-${p.sku}`}
                                  title="Strato non definito: apri il catalogo per gestire la quantità"
                                  style={{ flexShrink: 0 }}>
                            +
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div style={{ marginTop: 8, textAlign: "right" }}>
                  <Link to="/customer/catalog" data-testid="recent-view-all">
                    <Button size="sm" variant="ghost">Vai al catalogo →</Button>
                  </Link>
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Recent deliveries */}
        <Card data-testid="tile-recent-deliveries">
          <Card.Header><strong>Ultime consegne</strong></Card.Header>
          <Card.Body>
            {d.recent_deliveries.length === 0 ? (
              <p className="muted">Nessuna consegna recente.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {d.recent_deliveries.map((s) => {
                  const isHistoric = s.source === "history";
                  const key = s.shipment_id || `hist-${s.source_ref}-${s.picked_up_at}`;
                  const testId = s.shipment_number || `hist-${s.source_ref}`;
                  return (
                    <li key={key} data-testid={`delivery-${testId}`}
                        style={{ padding: "6px 0", borderBottom: "1px solid #eee" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {isHistoric ? (
                            <>
                              <strong>DDT {s.source_ref}</strong>
                              <div className="muted" style={{ fontSize: ".8rem" }}>
                                {s.items_count} articol{s.items_count === 1 ? "o" : "i"} · {Number(s.total_kg).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                                {s.picked_up_at && ` · ${fmtDate(s.picked_up_at)}`}
                              </div>
                            </>
                          ) : (
                            <>
                              <strong>{s.order_number}</strong> · {s.shipment_number}
                              <div className="muted" style={{ fontSize: ".8rem" }}>
                                {s.carrier_name || "—"}
                                {s.expected_delivery_date && ` · prev. ${fmtDate(s.expected_delivery_date)}`}
                              </div>
                            </>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column",
                                      alignItems: "flex-end", gap: 4 }}>
                          <Badge tone={s.status === "DELIVERED" ? "success" : "info"}>
                            {isHistoric ? "Storico" : (SHIPMENT_STATUS_LABEL[s.status] || s.status)}
                          </Badge>
                          {!isHistoric && (
                            <Link to={`/customer/orders/${s.order_id}`}
                                  data-testid={`delivery-open-${s.shipment_number}`}>
                              <Button size="sm">Apri</Button>
                            </Link>
                          )}
                          {/* Feb 2026 v34 — CTA "Apri DDT" future-ready.
                              Compare SOLO quando il backend valorizza
                              document_available=true (oggi sempre false:
                              storico e portale, PDF DDT non ancora importati).
                              Nessun link finto, nessun placeholder. */}
                          {s.document_available === true && s.document_url && (
                            <button type="button"
                                    data-testid={`delivery-doc-${testId}`}
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("access_token");
                                        const r = await fetch(
                                          `${process.env.REACT_APP_BACKEND_URL}${s.document_url}`,
                                          { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                                        if (!r.ok) { alert("Documento non disponibile"); return; }
                                        const raw = await r.blob();
                                        const pdfBlob = new Blob([raw], { type: "application/pdf" });
                                        const url = URL.createObjectURL(pdfBlob);
                                        window.open(url, "_blank", "noopener,noreferrer");
                                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                                      } catch (e) { alert("Errore apertura PDF"); }
                                    }}
                                    style={{ background: "transparent", border: "1px solid #94A3B8",
                                             color: "#334155", padding: "3px 8px",
                                             borderRadius: 6, fontSize: ".72rem", cursor: "pointer",
                                             display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <Paperclip size={12} aria-hidden="true" />
                              Apri DDT
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card.Body>
        </Card>

        {/* Shipping tiers */}
        <ShippingTiersCard
          tiersByRegion={d.shipping_tiers_by_region || {}}
          deliveryPoints={d.delivery_points}
          defaultDpId={d.default_delivery_point_id} />
      </div>

      {/* DP choice modal for new order */}
      {dpChoice && (
        <div role="dialog" data-testid="dp-choice-modal"
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
          <div style={{ background: "#fff", borderRadius: 12, width: "95%", maxWidth: 480, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Seleziona sede di consegna</h3>
            <p className="muted">Hai più sedi. Scegli quella per questo ordine.</p>
            <div style={{ display: "grid", gap: 8 }}>
              {d.delivery_points.map((dp) => (
                <Button key={dp.id}
                        onClick={() => { sessionStorage.setItem("preferred_dp", dp.id); nav("/customer/catalog"); }}
                        data-testid={`dp-choice-${dp.id}`}>
                  <strong>{dp.name}</strong> — {dp.city} ({dp.province})
                </Button>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Button onClick={() => setDpChoice(false)}>Annulla</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
